'use client';

import { useCallback } from 'react';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/utils/cn';
import { T_Symbol, T_Timeframe } from '@/ts/types/T_Chart';

interface I_ChartTabsProps {
  tokens: T_Symbol[];
  selectedToken?: string;
  timeframe?: T_Timeframe;
  className?: string;
  url?: string;
  variant?: 'default' | 'modal';
  setStateSymbol?: (stateSymbol: T_Symbol) => void;
  type?: 'prediction' | 'pool';
  poolSlugs?: string[];
  currentSlug?: string;
  poolSymbols?: string[];
}

export function ChartTab({
  tokens,
  selectedToken,
  timeframe,
  className = '',
  url,
  variant = 'default',
  setStateSymbol,
  type = 'prediction',
  poolSlugs,
  poolSymbols
}: I_ChartTabsProps) {
  const getTabItem = useCallback(
    ({ token }: { token: T_Symbol; index: number }) => {
      const getTf = () => {
        if (timeframe === '1H') {
          return '';
        } else {
          return `?tf=${timeframe}`;
        }
      };

      const tf = getTf();

      // For pools, use slug for navigation if available (default behavior)
      // For profile/pools page, use tokenSymbol instead
      let href = `${url}/${token.toLowerCase()}${tf}`;
      const isProfilePoolsPage = url === '/profile/pools';

      if (type === 'pool' && poolSymbols && poolSymbols.length > 0) {
        if (isProfilePoolsPage) {
          // For profile/pools page, use tokenSymbol for navigation
          const tokenSymbol = poolSymbols.find(
            sym => sym.toUpperCase() === token.toUpperCase()
          );
          if (tokenSymbol) {
            // Use tokenSymbol directly (e.g., "TxBTC" -> "txbtc")
            href = `${url}/${tokenSymbol.toLowerCase()}${tf}`;
          }
        } else if (poolSlugs && poolSlugs.length > 0) {
          // For other pages, use slug for navigation (existing logic)
          const tokenIndex = poolSymbols.findIndex(
            sym => sym.toUpperCase() === token.toUpperCase()
          );
          if (tokenIndex >= 0 && tokenIndex < poolSlugs.length) {
            href = `${url}/${poolSlugs[tokenIndex]}${tf}`;
          }
        }
      }

      let displayToken = token as string;
      if (type === 'pool' && displayToken.toUpperCase().startsWith('TX')) {
        displayToken = 'Tx' + displayToken.substring(2).toUpperCase();
      }

      if (variant === 'modal') {
        return (
          <span
            className="cursor-pointer flex w-full justify-center md:px-4 md:py-2.5 px-2.5 py-2"
            onClick={() => setStateSymbol?.(token)}
          >
            {displayToken}
          </span>
        );
      } else {
        return (
          <Link
            className="cursor-pointer flex w-full justify-center md:px-4 md:py-2.5 px-2.5 py-2"
            href={href}
          >
            {displayToken}
          </Link>
        );
      }
    },
    [variant, setStateSymbol, url, timeframe, type, poolSlugs, poolSymbols]
  );

  return (
    <div className={cn('overflow-x-auto w-full flex ', className)}>
      <div className="md:bg-none px-0 md:px-5 flex pt-2.5 border-b justify-between md:justify-center lg:justify-center border-base-border/20 min-w-fit w-full">
        <Tabs
          value={selectedToken}
          className="w-full max-w-(--container-width) px-4"
        >
          <TabsList className="bg-transparent h-auto p-0 w-full justify-between md:justify-center lg:justify-between">
            {tokens.map((token, index) => (
              <TabsTrigger
                key={token}
                value={token}
                className="text-base font-normal leading-normal
                         data-[state=active]:border-b-[3px] data-[state=active]:border-b-primary
                         data-[state=active]:text-primary data-[state=active]:font-bold
                         data-[state=inactive]:text-muted bg-transparent border-b-0 rounded-none
                         data-[state=active]:bg-transparent data-[state=inactive]:bg-transparent
                         focus-visible:ring-0 focus-visible:ring-offset-0"
              >
                {getTabItem({ token, index })}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
    </div>
  );
}
